import {
  reportCampaignExportQuerySchema,
  reportCampaignListQuerySchema,
  reportCampaignListResponseSchema,
  reportCampaignOptionsQuerySchema,
  reportCampaignOptionsResponseSchema,
  reportMessageDetailSchema,
  reportMessageExportQuerySchema,
  reportMessageListQuerySchema,
  reportMessageListResponseSchema,
  reportMessageOptionsQuerySchema,
  reportMessageOptionsResponseSchema,
  reportMessageSummaryQuerySchema,
  reportMessageSummaryResponseSchema,
  contactImportReportListQuerySchema,
  contactImportReportListResponseSchema,
  type ContactImportReportListQuery,
  type ContactImportReportListResponse,
  type ReportCampaignExportQuery,
  type ReportCampaignListQuery,
  type ReportCampaignListResponse,
  type ReportCampaignOptionsQuery,
  type ReportCampaignOptionsResponse,
  type ReportExportFormat,
  type ReportExportLocale,
  type ReportMessageDetail,
  type ReportMessageExportQuery,
  type ReportMessageListQuery,
  type ReportMessageListResponse,
  type ReportMessageOptionsQuery,
  type ReportMessageOptionsResponse,
  type ReportMessageSummaryQuery,
  type ReportMessageSummaryResponse,
} from "@multi-chat/shared";
import { apiGet } from "./client.js";

function reportsMessagesPath(organizationId: number): string {
  return `/organizations/${organizationId}/reports/messages`;
}

function reportsCampaignsPath(organizationId: number): string {
  return `/organizations/${organizationId}/reports/campaigns`;
}

function appendOptional(
  search: URLSearchParams,
  key: string,
  value: string | number | undefined,
): void {
  if (value === undefined || value === "") return;
  search.set(key, String(value));
}

function appendSharedFilters(
  search: URLSearchParams,
  parsed: {
    from?: string;
    to?: string;
    fromDate?: string;
    toDate?: string;
    platform?: string;
    origin?: string;
    endpointId?: number;
    campaignId?: number;
    template?: string;
    messageType?: string;
    search?: string;
  },
): void {
  if (parsed.fromDate && parsed.toDate) {
    appendOptional(search, "fromDate", parsed.fromDate);
    appendOptional(search, "toDate", parsed.toDate);
  } else {
    appendOptional(search, "from", parsed.from);
    appendOptional(search, "to", parsed.to);
  }
  appendOptional(search, "platform", parsed.platform);
  appendOptional(search, "origin", parsed.origin);
  appendOptional(search, "endpointId", parsed.endpointId);
  appendOptional(search, "campaignId", parsed.campaignId);
  appendOptional(search, "template", parsed.template);
  appendOptional(search, "messageType", parsed.messageType);
  appendOptional(search, "search", parsed.search);
}

/** Outgoing list always sends direction=OUTBOUND. */
export async function listReportMessages(
  organizationId: number,
  query: ReportMessageListQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageListResponse> {
  const parsed = reportMessageListQuerySchema.parse({
    ...query,
    direction: "OUTBOUND",
  });
  const search = new URLSearchParams();
  search.set("direction", "OUTBOUND");
  appendSharedFilters(search, parsed);
  appendOptional(search, "status", parsed.status);
  appendOptional(search, "statusBucket", parsed.statusBucket);
  appendOptional(search, "page", parsed.page);
  appendOptional(search, "pageSize", parsed.pageSize);
  appendOptional(search, "sort", parsed.sort);
  return reportMessageListResponseSchema.parse(
    await apiGet(`${reportsMessagesPath(organizationId)}?${search.toString()}`, signal),
  );
}

/** Incoming list always sends direction=INBOUND (Order 31C). */
export async function listIncomingReportMessages(
  organizationId: number,
  query: ReportMessageListQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageListResponse> {
  const parsed = reportMessageListQuerySchema.parse({
    ...query,
    direction: "INBOUND",
  });
  const search = new URLSearchParams();
  search.set("direction", "INBOUND");
  appendSharedFilters(search, parsed);
  appendOptional(search, "page", parsed.page);
  appendOptional(search, "pageSize", parsed.pageSize);
  appendOptional(search, "sort", parsed.sort);
  return reportMessageListResponseSchema.parse(
    await apiGet(`${reportsMessagesPath(organizationId)}?${search.toString()}`, signal),
  );
}

/** Unified Message Log — optional direction (omit = ALL). Does not call summary. */
export async function listMessageLogMessages(
  organizationId: number,
  query: ReportMessageListQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageListResponse> {
  const parsed = reportMessageListQuerySchema.parse(query);
  const search = new URLSearchParams();
  appendOptional(search, "direction", parsed.direction);
  appendSharedFilters(search, parsed);
  appendOptional(search, "status", parsed.status);
  appendOptional(search, "statusBucket", parsed.statusBucket);
  appendOptional(search, "page", parsed.page);
  appendOptional(search, "pageSize", parsed.pageSize);
  appendOptional(search, "sort", parsed.sort);
  return reportMessageListResponseSchema.parse(
    await apiGet(`${reportsMessagesPath(organizationId)}?${search.toString()}`, signal),
  );
}

export async function getReportMessagesSummary(
  organizationId: number,
  query: ReportMessageSummaryQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageSummaryResponse> {
  const parsed = reportMessageSummaryQuerySchema.parse(query);
  const search = new URLSearchParams();
  appendSharedFilters(search, parsed);
  appendOptional(search, "direction", parsed.direction);
  const qs = search.toString();
  return reportMessageSummaryResponseSchema.parse(
    await apiGet(
      `${reportsMessagesPath(organizationId)}/summary${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

export async function getIncomingReportMessagesSummary(
  organizationId: number,
  query: ReportMessageSummaryQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageSummaryResponse> {
  return getReportMessagesSummary(
    organizationId,
    { ...query, direction: "INBOUND" },
    signal,
  );
}

export async function getReportMessagesOptions(
  organizationId: number,
  query: ReportMessageOptionsQuery = {},
  signal?: AbortSignal,
): Promise<ReportMessageOptionsResponse> {
  const parsed = reportMessageOptionsQuerySchema.parse(query);
  const search = new URLSearchParams();
  appendOptional(search, "campaignSearch", parsed.campaignSearch);
  appendOptional(search, "templateSearch", parsed.templateSearch);
  const qs = search.toString();
  return reportMessageOptionsResponseSchema.parse(
    await apiGet(
      `${reportsMessagesPath(organizationId)}/options${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

/** Order 31E — Message Detail Drawer. */
export async function getReportMessageDetail(
  organizationId: number,
  messageId: string,
  signal?: AbortSignal,
): Promise<ReportMessageDetail> {
  return reportMessageDetailSchema.parse(
    await apiGet(`${reportsMessagesPath(organizationId)}/${encodeURIComponent(messageId)}`, signal),
  );
}

/** Order 31F — Campaign Report aggregate list. */
export async function listReportCampaigns(
  organizationId: number,
  query: ReportCampaignListQuery = {},
  signal?: AbortSignal,
): Promise<ReportCampaignListResponse> {
  const parsed = reportCampaignListQuerySchema.parse(query);
  const search = new URLSearchParams();
  if (parsed.fromDate && parsed.toDate) {
    appendOptional(search, "fromDate", parsed.fromDate);
    appendOptional(search, "toDate", parsed.toDate);
  } else {
    appendOptional(search, "from", parsed.from);
    appendOptional(search, "to", parsed.to);
  }
  appendOptional(search, "status", parsed.status);
  appendOptional(search, "endpointId", parsed.endpointId);
  appendOptional(search, "template", parsed.template);
  appendOptional(search, "search", parsed.search);
  appendOptional(search, "page", parsed.page);
  appendOptional(search, "pageSize", parsed.pageSize);
  appendOptional(search, "sort", parsed.sort);
  const qs = search.toString();
  return reportCampaignListResponseSchema.parse(
    await apiGet(
      `${reportsCampaignsPath(organizationId)}${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

export async function getReportCampaignsOptions(
  organizationId: number,
  query: ReportCampaignOptionsQuery = {},
  signal?: AbortSignal,
): Promise<ReportCampaignOptionsResponse> {
  const parsed = reportCampaignOptionsQuerySchema.parse(query);
  const search = new URLSearchParams();
  if (parsed.fromDate && parsed.toDate) {
    appendOptional(search, "fromDate", parsed.fromDate);
    appendOptional(search, "toDate", parsed.toDate);
  } else {
    appendOptional(search, "from", parsed.from);
    appendOptional(search, "to", parsed.to);
  }
  const qs = search.toString();
  return reportCampaignOptionsResponseSchema.parse(
    await apiGet(
      `${reportsCampaignsPath(organizationId)}/options${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

/** Order 31G — message export path (page/pageSize omitted). */
export function buildReportMessagesExportPath(
  organizationId: number,
  query: ReportMessageExportQuery,
): string {
  const parsed = reportMessageExportQuerySchema.parse(query);
  const search = new URLSearchParams();
  appendOptional(search, "direction", parsed.direction);
  appendSharedFilters(search, parsed);
  appendOptional(search, "status", parsed.status);
  appendOptional(search, "statusBucket", parsed.statusBucket);
  appendOptional(search, "sort", parsed.sort);
  search.set("format", parsed.format);
  appendOptional(search, "locale", parsed.locale);
  return `${reportsMessagesPath(organizationId)}/export?${search.toString()}`;
}

/** Order 31G — campaign export path (page/pageSize omitted; Drafts stay excluded). */
export function buildReportCampaignsExportPath(
  organizationId: number,
  query: ReportCampaignExportQuery,
): string {
  const parsed = reportCampaignExportQuerySchema.parse(query);
  const search = new URLSearchParams();
  if (parsed.fromDate && parsed.toDate) {
    appendOptional(search, "fromDate", parsed.fromDate);
    appendOptional(search, "toDate", parsed.toDate);
  } else {
    appendOptional(search, "from", parsed.from);
    appendOptional(search, "to", parsed.to);
  }
  appendOptional(search, "status", parsed.status);
  appendOptional(search, "endpointId", parsed.endpointId);
  appendOptional(search, "template", parsed.template);
  appendOptional(search, "search", parsed.search);
  appendOptional(search, "sort", parsed.sort);
  search.set("format", parsed.format);
  appendOptional(search, "locale", parsed.locale);
  return `${reportsCampaignsPath(organizationId)}/export?${search.toString()}`;
}

export async function listContactImportReports(
  organizationId: number,
  query: ContactImportReportListQuery,
  signal?: AbortSignal,
): Promise<ContactImportReportListResponse> {
  const parsed = contactImportReportListQuerySchema.parse(query);
  const search = new URLSearchParams();
  appendOptional(search, "page", parsed.page);
  appendOptional(search, "pageSize", parsed.pageSize);
  appendOptional(search, "search", parsed.search);
  appendOptional(search, "fromDate", parsed.fromDate);
  appendOptional(search, "toDate", parsed.toDate);
  const qs = search.toString();
  return contactImportReportListResponseSchema.parse(
    await apiGet(
      `/organizations/${organizationId}/reports/contact-imports${qs.length > 0 ? `?${qs}` : ""}`,
      signal,
    ),
  );
}

export function buildContactImportReportDownloadPath(
  organizationId: number,
  jobId: string,
  locale: ReportExportLocale,
): string {
  const search = new URLSearchParams();
  search.set("locale", locale);
  return `/organizations/${organizationId}/reports/contact-imports/${jobId}/report?${search.toString()}`;
}

export type { ReportExportFormat, ReportExportLocale };
